import React from 'react';

/**
 * Skeleton canônico de página de detalhe do portal (turma, lista de exercícios,
 * simulado, etc). Shared quando 3+ callers convergiram — nome antigo
 * (`SchoolClassDetailSkeleton`) mentia sobre o escopo.
 *
 * <p><b>Auto-contido:</b> inline styles + tokens globais (Shell.css é
 * eager-loaded). NÃO depende de `.p-detail-card`/`.ptabs`/`.pbody` porque
 * essas classes vivem no CSS específico da página, que é lazy — quando este
 * skeleton renderiza como Suspense fallback (primeira visita à rota), o CSS
 * do chunk ainda não chegou; classes viriam sem estilo. Usar `.sk` (que vive
 * em LoadingSystem.css, eager) + inline styles resolve o problema.
 *
 * <p><b>Anatomia:</b>
 * <ul>
 *   <li>Card wrapper — background paper, border ink-100, radius 12.
 *   <li>Tab bar — display flex gap 2, padding 0 20, border-bottom ink-100.
 *   N barras shimmer determinadas por {@code tabWidths}.
 *   <li>Body — padding 22×24, background paper, min-height 300.
 *   2 seções form (3 cols + 2 cols) — bate com FormGrid da tab general default.
 * </ul>
 *
 * <p><b>{@code tabWidths}:</b> array de larguras em px pras barras shimmer da
 * tab bar. Cada caller passa a quantidade de tabs real da sua tela — skeleton
 * mentiroso (prometer 4 tabs quando a tela tem 2) é o defeito de "prometer
 * forma que não vem", mesmo motivo que fez o skeleton do PagedList ganhar
 * larguras variadas. Default preserva o shape original (SchoolClassDetail).
 *
 * <p><b>Trade-off:</b> se o usuário chega direto na URL de uma tab que NÃO é
 * a default (form), o skeleton mostra form e o real é lista/agenda/plano →
 * salto. 90% das entradas caem na tab default do useState, aceitável.
 */
export default function PageDetailSkeleton({ tabWidths = [80, 90, 70, 100] }) {
	return (
		<div
			aria-busy="true"
			aria-live="polite"
			style={{
				background: 'var(--paper)',
				border: '1px solid var(--ink-100)',
				borderRadius: 12,
				overflow: 'hidden',
			}}
		>
			{/* Tab bar — omitida quando tabWidths é vazio (páginas lineares como
			    o import por IA não têm tabs; renderizar faixa vazia deixaria
			    linha horizontal fantasma no topo do card). */}
			{tabWidths.length > 0 && (
				<div
					aria-hidden="true"
					style={{
						display: 'flex',
						gap: 2,
						padding: '0 20px',
						borderBottom: '1px solid var(--ink-100)',
					}}
				>
					{tabWidths.map((w, i) => (
						<div key={i} style={{ padding: '12px 15px' }}>
							<span className="sk" style={{ width: w, height: 13, borderRadius: 3, display: 'block' }} />
						</div>
					))}
				</div>
			)}

			{/* Body */}
			<div
				aria-hidden="true"
				style={{ padding: '22px 24px', minHeight: 300 }}
			>
				{/* Section 1 — 3-col grid */}
				<div style={{ marginBottom: 24 }}>
					<span className="sk" style={{ width: 140, height: 10, marginBottom: 14, display: 'block' }} />
					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
						{[70, 82, 66].map((w, i) => (
							<div key={i}>
								<span className="sk" style={{ width: w, height: 9, marginBottom: 7, display: 'block' }} />
								<span className="sk" style={{ width: '100%', height: 38, borderRadius: 9, display: 'block' }} />
							</div>
						))}
					</div>
				</div>
				{/* Section 2 — 2-col grid, mais compacta */}
				<div>
					<span className="sk" style={{ width: 120, height: 10, marginBottom: 14, display: 'block' }} />
					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, maxWidth: 440 }}>
						{[60, 74].map((w, i) => (
							<div key={i}>
								<span className="sk" style={{ width: w, height: 9, marginBottom: 7, display: 'block' }} />
								<span className="sk" style={{ width: '100%', height: 38, borderRadius: 9, display: 'block' }} />
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
